import { useState } from "react";
import { useTranslation } from "react-i18next";

import { deleteBillingThunk } from "../../reducers/billingReducer";
import { useAppDispatch } from "../../store";
import { IModal } from "../../types/modal";
import { CustomButton, IButtonVariant } from "../CustomButton";
import { CustomModal } from "../CustomModal";
import { showCustomToast } from "../CustomToast";

import styles from "./DeleteBillingModal.module.scss";

type DeleteBillingModal = IModal & {
  billingId: number;
  billingYear: number;
};

export const DeleteBillingModal: React.FC<DeleteBillingModal> = ({
  isModalVisible,
  toggleModalVisibility,
  billingId,
  billingYear,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const handleDeleteBilling = async () => {
    setIsFetching(true);

    const result = await dispatch(deleteBillingThunk(billingId));

    if (deleteBillingThunk.fulfilled.match(result)) {
      showCustomToast(t("deleteBillingModal.deleteSuccess"), { type: "success" });
      toggleModalVisibility();
    } else if (deleteBillingThunk.rejected.match(result)) {
      setError(t(`billingApiErrors.${result.payload}`));
    }

    setIsFetching(false);
  };

  const clearModalState = () => {
    setError("");
  };

  const handleCloseModal = () => {
    clearModalState();
    toggleModalVisibility();
  };

  return (
    <CustomModal
      isModalVisible={isModalVisible}
      toggleModalVisibility={handleCloseModal}
      title={t("deleteBillingModal.title", { year: billingYear })}
      text={t("deleteBillingModal.text")}
    >
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.formContainer}>
        <CustomButton
          text={t("deleteBillingModal.cancel")}
          onClick={handleCloseModal}
          disabled={isFetching}
          variant={IButtonVariant.neutral}
        />
        <CustomButton text={t("deleteBillingModal.delete")} onClick={handleDeleteBilling} disabled={isFetching} />
      </div>
    </CustomModal>
  );
};
